import { useCallback, useMemo, useState } from 'react'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { getUserCookieValues, deleteUserCookies, storeUserData } from 'utils/userAuth'

export const useImpersonation = () => {
  const [impersonateError, setImpersonateError] = useState<string>('')
  const [userIdToImpersonate, setUserIdToImpersonate] = useState<number | null>(null)

  const impersonateParentUser = useCallback(
    async (impersonationReason: string) => {
      try {
        const response = await callApi({
          method: 'POST',
          id: userIdToImpersonate,
          body: {
            impersonationReason,
          },
          endpoint: ENDPOINTS.parentUsers,
          group: 'impersonate',
        })

        const { authToken, userEmail, userId, ...cookies } = getUserCookieValues()

        const { children, id, parentUserId } = response

        const impersonationData = {
          ...response,
          id: parentUserId ? id : children[0]?.id || id,
          parentUserId: parentUserId || id,
          impersonating_user_email: userEmail,
          impersonating_user_token: authToken,
          impersonating_user_id: userId,
          impersonating_cookies: JSON.stringify(cookies),
        }

        await deleteUserCookies(true)
        await storeUserData(impersonationData)

        global.location.reload()
      } catch (e) {
        setImpersonateError('There was an error requesting the data for impersonation')
      }
    },
    [userIdToImpersonate]
  )

  const stopImpersonation = useCallback(async () => {
    const {
      impersonatingCookies,
      impersonatingUserEmail,
      impersonatingUserId,
      impersonatingUserToken,
    } = getUserCookieValues()

    const impersonator = {
      id: impersonatingUserId,
      email: impersonatingUserEmail,
      authToken: impersonatingUserToken,
      ...JSON.parse(impersonatingCookies),
    }

    await deleteUserCookies(true)
    await storeUserData(impersonator)

    global.location.href = __ROOT__
  }, [])

  const hideImpersonateUserDialog = useCallback(() => {
    setUserIdToImpersonate(null)
  }, [])

  const isImpersonating = useMemo(() => {
    const { impersonatingUserToken } = getUserCookieValues()

    return !!impersonatingUserToken
  }, [])

  return {
    userIdToImpersonate,
    showImpersonateUserDialog: setUserIdToImpersonate,
    hideImpersonateUserDialog,
    impersonateParentUser,
    setUserIdToImpersonate,
    stopImpersonation,
    impersonateError,
    isImpersonating,
  }
}
